import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Container,
  FormFeedback,
} from "reactstrap"
import Select from "react-select"
import { useNavigate, useLocation } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useSelector, useDispatch } from "react-redux"

import { getAwardDetails, updateAward } from "../../../redux/actions/awards"
import { UPDATE_AWARD_RESET, GET_AWARD_DETAILS_RESET } from "../../../redux/types/awards"
import SuccessPopup from "../../../components/SuccessPopup"
import ErrorPopup from "../../../components/ErrorPopup"

// Converts "31 Mar 2026" → "2026-03-31" for input[type=date]
function parseDateToISO(dateStr) {
  if (!dateStr) return ""
  const months = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
  const parts = dateStr.trim().split(" ")
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10)
    const month = months[parts[1]]
    const year = parseInt(parts[2], 10)
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    }
  }
  // Fallback: already YYYY-MM-DD?
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10)
  return ""
}

function parseTime(timeStr) {
  // Converts "10:00AM" → "10:00" (HH:mm for input[type=time])
  if (!timeStr) return ""
  const match = timeStr.match(/(\d{1,2}):(\d{2})(AM|PM)/i)
  if (match) {
    let hour = parseInt(match[1], 10)
    const ampm = match[3].toUpperCase()
    if (ampm === "PM" && hour !== 12) hour += 12
    if (ampm === "AM" && hour === 12) hour = 0
    return `${String(hour).padStart(2, "0")}:${match[2]}`
  }
  // Fallback: already HH:mm?
  if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr
  return ""
}

function formatTime(timeStr) {
  // Converts "10:00" (HH:mm) → "10:00AM"
  if (!timeStr) return ""
  const [h, m] = timeStr.split(":")
  let hour = parseInt(h, 10)
  const ampm = hour >= 12 ? "PM" : "AM"
  hour = hour % 12 || 12
  return `${String(hour).padStart(2, "0")}:${m}${ampm}`
}

function EditAward() {
  document.title = "Edit Award | Admin Dashboard"

  const history = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const { url, enc_award_id } = location.state || {}

  const editAwardScreen = useSelector(state => state.screens.awards.editAward)
  const awardDetails = useSelector(state => state.awards.awardDetails)

  const [successOpen, setSuccessOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [dataLoaded, setDataLoaded] = useState(false)

  // Fetch award details on mount
  useEffect(() => {
    if (url) {
      dispatch(getAwardDetails(url))
    }
    return () => {
      dispatch({ type: GET_AWARD_DETAILS_RESET })
    }
  }, [])

  // Pre-populate form when awardDetails loads
  useEffect(() => {
    if (awardDetails && !dataLoaded) {
      // Parse time_slot ("10:00AM - 12:00PM") back to HH:mm for time inputs
      const timeParts = (awardDetails.time_slot || "").split(" - ")
      const rawStart = timeParts[0]?.trim() || ""
      const rawEnd = timeParts[1]?.trim() || ""

      formik.setValues({
        title: awardDetails.title || "",
        subtitle: awardDetails.subtitle || "",
        award_date: parseDateToISO(awardDetails.award_date),
        start_time: parseTime(rawStart),
        end_time: parseTime(rawEnd),
        status:
          awardDetails.status === 1 || awardDetails.status === true
            ? true
            : awardDetails.status === 0 || awardDetails.status === false
            ? false
            : null,
        google_address: awardDetails.google_address || "",
        address: awardDetails.address || "",
        description: awardDetails.description || "",
      })

      setDataLoaded(true)
    }
  }, [awardDetails])

  // Watch submit result
  useEffect(() => {
    if (editAwardScreen && editAwardScreen.success) {
      setSuccessOpen(true)
    } else if (editAwardScreen && editAwardScreen.error) {
      setErrorMsg(editAwardScreen.error || "Unable to update award.")
      setErrorOpen(true)
    }
  }, [editAwardScreen])

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      title: "",
      subtitle: "",
      award_date: "",
      start_time: "",
      end_time: "",
      status: null,
      google_address: "",
      address: "",
      description: "",
    },

    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      subtitle: Yup.string().required("Subtitle is required"),
      award_date: Yup.string().required("Date is required"),
      start_time: Yup.string().required("Start time is required"),
      end_time: Yup.string().required("End time is required"),
      status: Yup.mixed().test("is-boolean", "Status is required", val => val === true || val === false),
    }),

    onSubmit: values => {
      const time_slot = `${formatTime(values.start_time)} - ${formatTime(values.end_time)}`

      const data = {
        enc_awd_id: enc_award_id,
        title: values.title,
        subtitle: values.subtitle,
        award_date: values.award_date,
        time_slot,
        status: values.status,
        google_address: values.google_address,
        address: values.address,
        description: values.description,
      }

      dispatch(updateAward(data))
    },
  })

  return (
    <React.Fragment>
      <SuccessPopup
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        onConfirm={() => {
          setSuccessOpen(false)
          dispatch({ type: UPDATE_AWARD_RESET })
          history(-1)
        }}
        message={"Award updated successfully!"}
      />
      <ErrorPopup
        isOpen={errorOpen}
        toggle={() => {
          setErrorOpen(false)
          dispatch({ type: UPDATE_AWARD_RESET })
        }}
        message={errorMsg}
      />

      <div className="page-content">
        <Container fluid={true}>
          <div className="mb-3 pb-2"></div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="flex-grow-1">
              <h2 className="h4 nom-title mb-0">Edit Award</h2>
            </div>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    className="mt-4"
                    onSubmit={e => {
                      e.preventDefault()
                      formik.handleSubmit()
                      return false
                    }}
                    action="#"
                  >
                    {/* Title & Subtitle */}
                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="title">
                          Title
                        </Label>
                        <Input
                          name="title"
                          id="title"
                          className="form-control"
                          placeholder="Enter award title"
                          type="text"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.title || ""}
                          invalid={
                            formik.touched.title && formik.errors.title
                              ? true
                              : false
                          }
                        />
                        {formik.touched.title && formik.errors.title ? (
                          <FormFeedback type="invalid">
                            {formik.errors.title}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="subtitle">
                          Subtitle
                        </Label>
                        <Input
                          name="subtitle"
                          id="subtitle"
                          className="form-control"
                          placeholder="Enter award subtitle"
                          type="text"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.subtitle || ""}
                          invalid={
                            formik.touched.subtitle && formik.errors.subtitle
                              ? true
                              : false
                          }
                        />
                        {formik.touched.subtitle && formik.errors.subtitle ? (
                          <FormFeedback type="invalid">
                            {formik.errors.subtitle}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </div>

                    {/* Date, Start Time, End Time */}
                    <div className="row">
                      <div className="mb-3 col-md-4">
                        <label className="form-label" htmlFor="award_date">
                          Date
                        </label>
                        <input
                          type="date"
                          id="award_date"
                          className={`form-control ${
                            formik.touched.award_date &&
                            formik.errors.award_date
                              ? "is-invalid"
                              : ""
                          }`}
                          {...formik.getFieldProps("award_date")}
                        />
                        <div className="invalid-feedback">
                          {formik.errors.award_date}
                        </div>
                      </div>

                      <div className="mb-3 col-md-4">
                        <label className="form-label" htmlFor="start_time">
                          Start Time
                        </label>
                        <input
                          type="time"
                          id="start_time"
                          className={`form-control ${
                            formik.touched.start_time &&
                            formik.errors.start_time
                              ? "is-invalid"
                              : ""
                          }`}
                          {...formik.getFieldProps("start_time")}
                        />
                        <div className="invalid-feedback">
                          {formik.errors.start_time}
                        </div>
                      </div>

                      <div className="mb-3 col-md-4">
                        <label className="form-label" htmlFor="end_time">
                          End Time
                        </label>
                        <input
                          type="time"
                          id="end_time"
                          className={`form-control ${
                            formik.touched.end_time && formik.errors.end_time
                              ? "is-invalid"
                              : ""
                          }`}
                          {...formik.getFieldProps("end_time")}
                        />
                        <div className="invalid-feedback">
                          {formik.errors.end_time}
                        </div>
                      </div>
                    </div>

                    {/* Status & Google Address */}
                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="status">
                          Status
                        </Label>
                        <Select
                          id="status"
                          name="status"
                          value={
                            [
                              { value: true, label: "Active" },
                              { value: false, label: "Inactive" },
                            ].find(
                              opt => opt.value === formik.values.status
                            ) || null
                          }
                          onChange={selectedOption => {
                            formik.setFieldValue(
                              "status",
                              selectedOption ? selectedOption.value : null
                            )
                          }}
                          onBlur={formik.handleBlur}
                          options={[
                            { value: true, label: "Active" },
                            { value: false, label: "Inactive" },
                          ]}
                          classNamePrefix="select2-selection"
                          className={
                            formik.touched.status && formik.errors.status
                              ? "is-invalid"
                              : ""
                          }
                        />
                        {formik.touched.status && formik.errors.status && (
                          <FormFeedback className="d-block" type="invalid">
                            {formik.errors.status}
                          </FormFeedback>
                        )}
                      </div>

                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="google_address">
                          Google Address
                        </Label>
                        <Input
                          name="google_address"
                          id="google_address"
                          className="form-control"
                          placeholder="Enter Google Maps address link"
                          type="text"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.google_address || ""}
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="row">
                      <div className="mb-3 col-md-12">
                        <Label className="form-label" htmlFor="address">
                          Address
                        </Label>
                        <textarea
                          name="address"
                          id="address"
                          className="form-control"
                          placeholder="Enter venue address"
                          rows="3"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.address || ""}
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row">
                      <div className="mb-3 col-md-12">
                        <Label className="form-label" htmlFor="description">
                          Description
                        </Label>
                        <textarea
                          name="description"
                          id="description"
                          className="form-control"
                          placeholder="Enter award description"
                          rows="4"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.description || ""}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-2 mt-2">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={editAwardScreen.loading}
                      >
                        {editAwardScreen.loading
                          ? "Updating..."
                          : "Update Award"}
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => history(-1)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default EditAward
